<template>
  <div class="apply-now-section">
    <!-- Background Elements -->
    <div class="section-background">
      <div class="bg-pattern"></div>
      <div class="floating-elements">
        <div class="floating-element element-1"></div>
        <div class="floating-element element-2"></div>
        <div class="floating-element element-3"></div>
      </div>
    </div>

    <div class="container mx-auto px-6 py-16">
      <!-- Section Header -->
      <div class="section-header text-center mb-16">
        <div class="header-accent"></div>
        <h2 class="section-title">
          Join Our <span class="highlight">Team</span> 
        </h2>
        <p class="section-subtitle">
          Build Your Career with STE Tank & Equipment
        </p>
        <div class="title-divider"></div>
      </div>

      <!-- Main Content Grid -->
      <div class="content-grid">
        <!-- Left Side - Company Culture & Benefits -->
        <div class="culture-section">
          <div class="culture-card">
            <div class="card-header">
              <font-awesome-icon :icon="['fas', 'users']" class="card-icon" />
              <h3>Why Choose STE?</h3>
            </div>
            <div class="card-content">
              <div class="benefit-list">
                <div class="benefit-item">
                  <font-awesome-icon :icon="['fas', 'award']" class="benefit-icon" />
                  <span>Industry-Leading Manufacturing Excellence</span>
                </div>
                <div class="benefit-item">
                  <font-awesome-icon :icon="['fas', 'tools']" class="benefit-icon" />
                  <span>State-of-the-Art Equipment & Technology</span>
                </div>
                <div class="benefit-item">
                  <font-awesome-icon :icon="['fas', 'graduation-cap']" class="benefit-icon" />
                  <span>Continuous Learning & Development</span>
                </div>
                <div class="benefit-item">
                  <font-awesome-icon :icon="['fas', 'handshake']" class="benefit-icon" />
                  <span>Collaborative Team Environment</span>
                </div>
                <div class="benefit-item">
                  <font-awesome-icon :icon="['fas', 'heart']" class="benefit-icon" />
                  <span>Comprehensive Benefits Package</span>
                </div>
                <div class="benefit-item">
                  <font-awesome-icon :icon="['fas', 'chart-line']" class="benefit-icon" />
                  <span>Career Growth Opportunities</span>
                </div>
              </div>
            </div>
          </div>
        </div>

          <!-- Company Values Highlight -->
          <div class="values-highlight">
            <h4>Things we Value!</h4>
            <div class="values-grid">
              <div class="value-item">
                <div class="value-icon">
                  <font-awesome-icon :icon="['fas', 'bullseye']" />
                </div>
                <span>Precision</span>
              </div>
              <div class="value-item">
                <div class="value-icon">
                  <font-awesome-icon :icon="['fas', 'shield-alt']" />
                </div>
                <span>Quality</span>
              </div>
              <div class="value-item">
                <div class="value-icon">
                  <font-awesome-icon :icon="['fas', 'rocket']" />
                </div>
                <span>Innovation</span>
              </div>
              <div class="value-item">
                <div class="value-icon">
                  <font-awesome-icon :icon="['fas', 'users-cog']" />
                </div>
                <span>Teamwork</span>
              </div>
            </div>
          </div>

        <!-- Right Side - Application Form -->
        <div class="application-section">
          <div class="form-container">
            <div class="form-header">
              <font-awesome-icon :icon="['fas', 'file-contract']" class="form-icon" />
              <h3>Employment Application</h3>
              <p>Complete your application to join our manufacturing excellence team</p>
            </div>

            <!-- Application Form -->
            <form @submit.prevent="submitApplication" class="application-form">
              <!-- Personal Information Section -->
              <div class="form-section">
                <h4 class="section-title-small">
                  <font-awesome-icon :icon="['fas', 'user']" class="section-icon" />
                  Personal Information
                </h4>
                
                <div class="form-grid">
                  <div class="form-group">
                    <label for="firstName">First Name *</label>
                    <input 
                      type="text" 
                      id="firstName" 
                      v-model="form.firstName" 
                      required 
                      class="form-input"
                      :class="{ 'error': errors.firstName }"
                    />
                    <span v-if="errors.firstName" class="error-message">{{ errors.firstName }}</span>
                  </div>
                  
                  <div class="form-group">
                    <label for="lastName">Last Name *</label>
                    <input 
                      type="text" 
                      id="lastName" 
                      v-model="form.lastName" 
                      required 
                      class="form-input"
                      :class="{ 'error': errors.lastName }"
                    />
                    <span v-if="errors.lastName" class="error-message">{{ errors.lastName }}</span>
                  </div>
                </div>

                <div class="form-group">
                  <label for="email">Email Address *</label>
                  <input 
                    type="email" 
                    id="email" 
                    v-model="form.email" 
                    required 
                    class="form-input"
                    :class="{ 'error': errors.email }"
                  />
                  <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
                </div>

                <div class="form-grid">
                  <div class="form-group">
                    <label for="phone">Phone Number *</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      v-model="form.phone" 
                      required 
                      class="form-input"
                      :class="{ 'error': errors.phone }"
                    />
                    <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
                  </div>

                </div>

                <div class="form-group">
                  <label for="address">Street Address *</label>
                  <input 
                    type="text" 
                    id="address" 
                    v-model="form.address" 
                    required 
                    class="form-input"
                    :class="{ 'error': errors.address }"
                  />
                  <span v-if="errors.address" class="error-message">{{ errors.address }}</span>
                </div>

                <div class="form-grid">
                  <div class="form-group">
                    <label for="city">City *</label>
                    <input 
                      type="text" 
                      id="city" 
                      v-model="form.city" 
                      required 
                      class="form-input"
                      :class="{ 'error': errors.city }"
                    />
                    <span v-if="errors.city" class="error-message">{{ errors.city }}</span>
                  </div>
                  
                  <div class="form-group">
                    <label for="state">State</label>
                    <input 
                      type="text" 
                      id="state" 
                      v-model="form.state" 
                      class="form-input"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label for="zipCode">ZIP Code *</label>
                    <input 
                      type="text" 
                      id="zipCode" 
                      v-model="form.zipCode" 
                      required 
                      class="form-input"
                      :class="{ 'error': errors.zipCode }"
                    />
                    <span v-if="errors.zipCode" class="error-message">{{ errors.zipCode }}</span>
                  </div>
                </div>
              </div>

              <!-- Position Information Section -->
              <div class="form-section">
                <h4 class="section-title-small">
                  <font-awesome-icon :icon="['fas', 'briefcase']" class="section-icon" />
                  Position Information
                </h4>
                
                <div class="form-group">
                  <label for="position">Position Applying For *</label>
                  <select 
                    id="position" 
                    v-model="form.position" 
                    required 
                    class="form-input"
                    :class="{ 'error': errors.position }"
                  >
                    <option value="">Select a position...</option>
                    <option value="Welder">Welder</option>
                    <option value="Fabricator">Fabricator</option>
                    <option value="CNC Operator">CNC Operator</option>
                    <option value="Quality Inspector">Quality Inspector</option>
                    <option value="Production Supervisor">Production Supervisor</option>
                    <option value="Maintenance Technician">Maintenance Technician</option>
                    <option value="Office/Administrative">Office/Administrative</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Other">Other</option>
                  </select>
                  <span v-if="errors.position" class="error-message">{{ errors.position }}</span>
                </div>

                <div class="form-group" v-if="form.position === 'Other'">
                  <label for="positionOther">Specify Position</label>
                  <input 
                    type="text" 
                    id="positionOther" 
                    v-model="form.positionOther" 
                    class="form-input"
                    placeholder="Please specify the position you're applying for"
                  />
                </div>

                <div class="form-grid">
                  <div class="form-group">
                    <label for="desiredSalary">Desired Salary</label>
                    <input 
                      type="text" 
                      id="desiredSalary" 
                      v-model="form.desiredSalary" 
                      class="form-input"
                      placeholder="e.g., $45,000 or Negotiable"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label for="startDate">Available Start Date</label>
                    <input 
                      type="date" 
                      id="startDate" 
                      v-model="form.startDate" 
                      class="form-input"
                    />
                  </div>
                </div>

                <div class="form-group">
                  <label for="availability">Work Schedule Availability</label>
                  <div class="checkbox-group">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.availability" value="Full-time" />
                      <span class="checkmark"></span>
                      Full-time
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.availability" value="Part-time" />
                      <span class="checkmark"></span>
                      Part-time
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.availability" value="Overtime" />
                      <span class="checkmark"></span>
                      Overtime as needed
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.availability" value="Weekends" />
                      <span class="checkmark"></span>
                      Weekend work
                    </label>
                  </div>
                </div>
              </div>

              <!-- Experience & Skills Section -->
              <div class="form-section">
                <h4 class="section-title-small">
                  <font-awesome-icon :icon="['fas', 'tools']" class="section-icon" />
                  Experience & Skills
                </h4>
                
                <!-- Resume Upload -->
                <div class="form-group">
                  <label for="resume">Resume Upload <span class="file-note">(PDF, DOC, DOCX - Max 100KB)</span></label>
                  <div class="file-upload-container">
                    <input 
                      type="file" 
                      id="resume" 
                      @change="handleResumeUpload"
                      accept=".pdf,.doc,.docx"
                      class="file-input"
                      :class="{ 'error': errors.resume }"
                    />
                    <div class="file-upload-display" :class="{ 'has-file': form.resumeFile }">
                      <div class="file-upload-content">
                        <font-awesome-icon 
                          :icon="form.resumeFile ? ['fas', 'file-check'] : ['fas', 'cloud-upload-alt']" 
                          class="file-icon"
                          :class="{ 'uploaded': form.resumeFile }"
                        />
                        <span class="file-text">
                          {{ form.resumeFile ? form.resumeFile.name : 'Drag file or click to upload' }}
                        </span>
                        <span v-if="form.resumeFile" class="file-size">
                          {{ formatFileSize(form.resumeFile.size) }}
                        </span>
                        <button 
                          v-if="form.resumeFile" 
                          type="button" 
                          @click="removeResume"
                          class="remove-file-btn"
                        >
                          <font-awesome-icon :icon="['fas', 'times']" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <p class="form-note">
                    <strong>Please include:</strong> Complete employment history, education, certifications, and professional references. 
                    This will serve as your comprehensive work background documentation.
                  </p>
                  <span v-if="errors.resume" class="error-message">{{ errors.resume }}</span>
                </div>
                
                <div class="form-group">
                  <label for="experience">Additional Work Experience Details</label>
                  <textarea 
                    id="experience" 
                    v-model="form.experience" 
                    rows="4" 
                    class="form-textarea"
                    placeholder="Describe any additional relevant work experience, specific projects, or achievements not covered in your resume..."
                  ></textarea>
                </div>

                <div class="form-group">
                  <label for="certifications">Additional Certifications & Training</label>
                  <textarea 
                    id="certifications" 
                    v-model="form.certifications" 
                    rows="3" 
                    class="form-textarea"
                    placeholder="List any additional certifications, training programs, or technical education beyond what's in your resume..."
                  ></textarea>
                </div>

                <div class="form-group">
                  <label for="skills">Technical Skills</label>
                  <div class="checkbox-group">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.skills" value="MIG Welding" />
                      <span class="checkmark"></span>
                      MIG Welding
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.skills" value="TIG Welding" />
                      <span class="checkmark"></span>
                      TIG Welding
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.skills" value="Arc Welding" />
                      <span class="checkmark"></span>
                      Arc Welding
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.skills" value="CNC Operation" />
                      <span class="checkmark"></span>
                      CNC Operation
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.skills" value="Blueprint Reading" />
                      <span class="checkmark"></span>
                      Blueprint Reading
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.skills" value="Quality Inspection" />
                      <span class="checkmark"></span>
                      Quality Inspection
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.skills" value="Fabrication" />
                      <span class="checkmark"></span>
                      Metal Fabrication
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.skills" value="Forklift Operation" />
                      <span class="checkmark"></span>
                      Forklift Operation
                    </label>
                  </div>
                </div>
              </div>

              <!-- Legal Questions Section -->
              <div class="form-section">
                <h4 class="section-title-small">
                  <font-awesome-icon :icon="['fas', 'gavel']" class="section-icon" />
                  Legal Information
                </h4>
                
                <div class="form-group">
                  <label class="radio-group-label">Are you legally authorized to work in the United States? *</label>
                  <div class="radio-group">
                    <label class="radio-label">
                      <input type="radio" v-model="form.workAuthorization" value="Yes" required />
                      <span class="radio-mark"></span>
                      Yes
                    </label>
                    <label class="radio-label">
                      <input type="radio" v-model="form.workAuthorization" value="No" required />
                      <span class="radio-mark"></span>
                      No
                    </label>
                  </div>
                  <span v-if="errors.workAuthorization" class="error-message">{{ errors.workAuthorization }}</span>
                </div>
              </div>

              <!-- Additional Information Section -->
              <div class="form-section">
                <h4 class="section-title-small">
                  <font-awesome-icon :icon="['fas', 'comment']" class="section-icon" />
                  Additional Information
                </h4>
                
                <div class="form-group">
                  <label for="additionalInfo">Why do you want to work at STE Tank & Equipment?</label>
                  <textarea 
                    id="additionalInfo" 
                    v-model="form.additionalInfo" 
                    rows="4" 
                    class="form-textarea"
                    placeholder="Tell us what interests you about working for STE and how you would contribute to our team..."
                  ></textarea>
                </div>

                <div class="form-group">
                  <label for="referral">How did you hear about this position?</label>
                  <select id="referral" v-model="form.referral" class="form-input">
                    <option value="">Select...</option>
                    <option value="Website">Company Website</option>
                    <option value="Job Board">Job Board</option>
                    <option value="Employee Referral">Employee Referral</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Walk-in">Walk-in</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <!-- Electronic Signature Section -->
              <div class="form-section">
                <h4 class="section-title-small">
                  <font-awesome-icon :icon="['fas', 'signature']" class="section-icon" />
                  Electronic Signature
                </h4>
                
                <div class="signature-container">
                  <div class="signature-header">
                    <p>Please sign below to certify that the information provided is true and complete:</p>
                  </div>
                  
                  <div class="signature-canvas-container">
                    <canvas 
                      ref="signatureCanvas" 
                      width="400" 
                      height="150" 
                      class="signature-canvas"
                      @mousedown="startSigning"
                      @mousemove="sign"
                      @mouseup="stopSigning"
                      @touchstart="startSigning"
                      @touchmove="sign"
                      @touchend="stopSigning"
                    ></canvas>
                    <div class="signature-placeholder" v-if="!hasSignature">
                      Sign here with your mouse or finger
                    </div>
                  </div>
                  
                  <div class="signature-controls">
                    <button type="button" @click="clearSignature" class="clear-signature-btn">
                      <font-awesome-icon :icon="['fas', 'eraser']" />
                      Clear Signature
                    </button>
                  </div>
                  
                  <div class="signature-agreement">
                    <p class="signature-text">
                      By signing above, I certify that all information provided in this application is true and complete. 
                      I understand that any false information may lead to disqualification from employment or termination if hired.
                    </p>
                  </div>
                </div>
                <span v-if="errors.signature" class="error-message">{{ errors.signature }}</span>
              </div>

              <!-- reCAPTCHA -->
              <div class="form-section">
                <div class="recaptcha-container">
                  <div id="careers-recaptcha-container"></div>
                  <span v-if="errors.recaptcha" class="error-message">{{ errors.recaptcha }}</span>
                </div>
              </div>

              <!-- Submit Button -->
              <div class="form-submit">
                <button 
                  type="submit" 
                  class="submit-btn"
                  :disabled="isSubmitting"
                  :class="{ 'submitting': isSubmitting }"
                >
                  <font-awesome-icon 
                    v-if="isSubmitting" 
                    :icon="['fas', 'spinner']" 
                    spin 
                    class="submit-icon" 
                  />
                  <font-awesome-icon 
                    v-else 
                    :icon="['fas', 'paper-plane']" 
                    class="submit-icon" 
                  />
                  <span>{{ isSubmitting ? 'Submitting Application...' : 'Submit Application' }}</span>
                </button>
              </div>
            </form>

            <!-- Success Message -->
            <div v-if="submitSuccess" class="success-message">
              <div class="success-content">
                <font-awesome-icon :icon="['fas', 'check-circle']" class="success-icon" />
                <h3>Application Submitted Successfully!</h3>
                <p>Thank you for your interest in joining STE Tank & Equipment. We will review your application and contact you if there's a match for our current openings.</p>
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="submitError" class="error-message-container">
              <div class="error-content">
                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="error-icon" />
                <h3>Submission Error</h3>
                <p>{{ submitError }}</p>
                <button @click="submitError = ''" class="error-dismiss">Try Again</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import emailjs from '@emailjs/browser';

export default {
  name: 'ApplyNow',
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        position: '',
        positionOther: '',
        desiredSalary: '',
        startDate: '',
        availability: [],
        resumeFile: null,
        experience: '',
        certifications: '',
        skills: [],
        workAuthorization: '',
        additionalInfo: '',
        referral: '',
        signature: ''
      },
      errors: {},
      isSubmitting: false,
      submitSuccess: false,
      submitError: '',
      isSigningActive: false,
      hasSignature: false,
      recaptchaLoaded: false,
      recaptchaResponse: ''
    };
  },
  mounted() {
    // Initialize EmailJS
    this.initializeEmailJS();
    this.loadRecaptcha();
    this.initializeSignatureCanvas();
  },
  methods: {
    initializeEmailJS() {
      // Initialize EmailJS with your public key
      console.log('Initializing EmailJS...');
      if (typeof emailjs !== 'undefined') {
        emailjs.init('YOUR_PUBLIC_KEY'); // Replace with your actual public key
        console.log('EmailJS initialized successfully');
      } else {
        console.error('EmailJS library not loaded');
      }
    },
    loadRecaptcha() {
      // Load Google reCAPTCHA script
      if (!window.grecaptcha) {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?onload=onCareersRecaptchaLoad&render=explicit';
        script.async = true;
        script.defer = true;
        
        // Define global callback
        window.onCareersRecaptchaLoad = () => {
          this.renderRecaptcha();
        };
        
        document.head.appendChild(script);
      } else {
        this.renderRecaptcha();
      }
    },

    renderRecaptcha() {
      if (window.grecaptcha && document.getElementById('careers-recaptcha-container')) {
        window.grecaptcha.render('careers-recaptcha-container', {
          'sitekey': '6LfnNl4rAAAAACOnTCwnPDYztVqtjQuSIdsDNPNk', // Using same reCAPTCHA key as ServiceContactSection
          'callback': (response) => {
            this.recaptchaResponse = response;
            this.errors.recaptcha = '';
          },
          'expired-callback': () => {
            this.recaptchaResponse = '';
          }
        });
        this.recaptchaLoaded = true;
      }
    },

    initializeSignatureCanvas() {
      const canvas = this.$refs.signatureCanvas;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = '#8f0000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        
        // Set white background
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    },

    startSigning(e) {
      e.preventDefault();
      this.isSigningActive = true;
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      const ctx = canvas.getContext('2d');
      
      const x = (e.clientX || e.touches[0].clientX) - rect.left;
      const y = (e.clientY || e.touches[0].clientY) - rect.top;
      
      ctx.beginPath();
      ctx.moveTo(x, y);
    },

    sign(e) {
      if (!this.isSigningActive) return;
      e.preventDefault();
      
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      const ctx = canvas.getContext('2d');
      
      const x = (e.clientX || e.touches[0].clientX) - rect.left;
      const y = (e.clientY || e.touches[0].clientY) - rect.top;
      
      ctx.lineTo(x, y);
      ctx.stroke();
      
      this.hasSignature = true;
      this.errors.signature = '';
    },

    stopSigning() {
      this.isSigningActive = false;
      // Convert canvas to base64 for form submission
      this.form.signature = this.$refs.signatureCanvas.toDataURL();
    },

    clearSignature() {
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Reset background
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      this.hasSignature = false;
      this.form.signature = '';
    },

    handleResumeUpload(event) {
      const file = event.target.files[0];
      
      if (!file) return;
      
      // Clear any previous errors
      this.errors.resume = '';
      
      // File size validation (100KB = 100 * 1024 bytes)
      const maxSize = 100 * 1024;
      if (file.size > maxSize) {
        this.errors.resume = 'File size must be less than 100KB';
        event.target.value = '';
        return;
      }
      
      // File type validation
      const allowedTypes = [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        this.errors.resume = 'Please upload a DOC, or DOCX file';
        event.target.value = '';
        return;
      }
      
      // Convert file to base64 for EmailJS
      const reader = new FileReader();
      reader.onload = (e) => {
        this.form.resumeFile = {
          name: file.name,
          size: file.size,
          type: file.type,
          data: e.target.result.split(',')[1] // Remove data:application/pdf;base64, prefix
        };
      };
      
      reader.onerror = () => {
        this.errors.resume = 'Error reading file. Please try again.';
        event.target.value = '';
      };
      
      reader.readAsDataURL(file);
    },

    removeResume() {
      this.form.resumeFile = null;
      // Clear the file input
      const fileInput = document.getElementById('resume');
      if (fileInput) {
        fileInput.value = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    validateForm() {
      this.errors = {};
      
      // Required field validation
      const requiredFields = [
        'firstName', 'lastName', 'email', 'phone', 'address', 
        'city', 'zipCode', 'position', 'workAuthorization'
      ];
      
      requiredFields.forEach(field => {
        if (!this.form[field] || this.form[field].trim() === '') {
          this.errors[field] = 'This field is required';
        }
      });

      // Resume file validation was removed

      // Email validation
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.form.email && !emailPattern.test(this.form.email)) {
        this.errors.email = 'Please enter a valid email address';
      }

      // Phone validation
      const phonePattern = /^[\+]?[1-9][\d]{0,15}$/;
      if (this.form.phone && !phonePattern.test(this.form.phone.replace(/\D/g, ''))) {
        this.errors.phone = 'Please enter a valid phone number';
      }

      // Signature validation
      if (!this.hasSignature) {
        this.errors.signature = 'Electronic signature is required';
      }

      // reCAPTCHA validation
      if (!this.recaptchaResponse) {
        this.errors.recaptcha = 'Please complete the reCAPTCHA verification';
      }

      return Object.keys(this.errors).length === 0;
    },

    async submitApplication() {
      if (!this.validateForm()) {
        // Scroll to first error without zoom/centering
        const firstError = Object.keys(this.errors)[0];
        const errorElement = document.getElementById(firstError);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      this.isSubmitting = true;
      this.submitError = '';

      try {
        // Prepare form data for EmailJS
        const referenceNumber = `APP-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
        
        const templateParams = {
          to_email: 'hr@ste-usa.com', // Replace with your email
          reference_number: referenceNumber,
          from_name: `${this.form.firstName} ${this.form.lastName}`,
          applicant_email: this.form.email,
          applicant_phone: this.form.phone,
          position: this.form.position === 'Other' ? this.form.positionOther : this.form.position,
          address: `${this.form.address}, ${this.form.city}, ${this.form.state} ${this.form.zipCode}`,
          desired_salary: this.form.desiredSalary || 'Not specified',
          start_date: this.form.startDate || 'ASAP',
          availability: this.form.availability.length > 0 ? this.form.availability.join(', ') : 'Not specified',
          experience: this.form.experience || 'No experience provided',
          certifications: this.form.certifications || 'No certifications listed',
          skills: this.form.skills.length > 0 ? this.form.skills.join(', ') : 'No skills selected',
          work_authorization: this.form.workAuthorization,
          additional_info: this.form.additionalInfo || 'No additional information provided',
          referral: this.form.referral || 'Not specified',
          signature_data: this.form.signature,
          submission_date: new Date().toLocaleDateString(),
          submission_time: new Date().toLocaleTimeString(),
          // File attachment for EmailJS
          resumeFile: this.form.resumeFile ? this.form.resumeFile.data : null
        };

        // Send email using EmailJS
        await emailjs.send(
          'service_l1abw81', // Replace with your EmailJS service ID
          'template_3qevb9d', // Replace with your EmailJS template ID
          templateParams,
          'NHRxQRBTZkiJcFeub' // Replace with your EmailJS public key
        );

        this.submitSuccess = true;
        this.resetForm();
        
        // Scroll to success message
        setTimeout(() => {
          const successElement = document.querySelector('.success-message');
          if (successElement) {
            successElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);

      } catch (error) {
        console.error('Form submission error:', error);
        
        // Provide more specific error messages
        let errorMessage = 'There was an error submitting your application. ';
        
        if (error.message && error.message.includes('EmailJS')) {
          errorMessage += 'EmailJS configuration error. Please contact support.';
        } else if (error.status === 400) {
          errorMessage += 'Please check that all required fields are completed correctly and try again.';
        } else if (error.status === 422) {
          errorMessage += 'There was an issue with the file attachment. Please try uploading a different file.';
        } else {
          errorMessage += 'Please try again or contact us directly at hr@ste-usa.com';
        }
        
        this.submitError = errorMessage;
      } finally {
        this.isSubmitting = false;
      }
    },

    resetForm() {
      this.form = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        position: '',
        positionOther: '',
        desiredSalary: '',
        startDate: '',
        availability: [],
        resumeFile: null,
        experience: '',
        certifications: '',
        skills: [],
        workAuthorization: '',
        additionalInfo: '',
        referral: '',
        signature: ''
      };
      
      this.clearSignature();
      this.errors = {};
      
      // Reset reCAPTCHA
      if (window.grecaptcha) {
        window.grecaptcha.reset();
        this.recaptchaResponse = '';
      }
    }
  }
};
</script>

<style scoped>
.apply-now-section {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg, 
    rgba(29, 30, 38, 0.95) 0%, 
    rgba(42, 43, 56, 0.95) 50%,
    rgba(29, 30, 38, 0.95) 100%
  );
  overflow: hidden;
  color: #ffffff;
}

/* Background Elements */
.section-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.bg-pattern {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.1;
}

.floating-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.floating-element {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(143, 0, 0, 0.3) 0%, transparent 70%);
  animation: float 8s ease-in-out infinite;
}

.element-1 {
  top: 10%;
  left: 10%;
  width: 120px;
  height: 120px;
  animation-delay: 0s;
}

.element-2 {
  top: 60%;
  right: 15%;
  width: 80px;
  height: 80px;
  animation-delay: 2s;
}

.element-3 {
  bottom: 20%;
  left: 20%;
  width: 100px;
  height: 100px;
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-20px) scale(1.05);
  }
}

/* Section Header */
.section-header {
  position: relative;
  z-index: 10;
  max-width: 800px;
  margin: 0 auto;
}

.header-accent {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #8f0000, rgba(143, 0, 0, 0.3));
  margin: 0 auto 2rem;
}

.section-title {
  font-size: 3rem;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.section-title .highlight {
  background: linear-gradient(135deg, #8f0000 0%, #a22a21 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.section-subtitle {
  font-size: 1.25rem;
  color: rgba(183, 185, 186, 0.9);
  margin-bottom: 2rem;
  line-height: 1.6;
}

.title-divider {
  width: 100px;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(143, 0, 0, 0.8), transparent);
  margin: 0 auto;
}

/* Main Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
}

@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
}

/* Culture Section - keeping existing styles */
.culture-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.culture-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.culture-card:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.card-icon {
  font-size: 1.5rem;
  color: #8f0000;
}

.card-header h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffffff;
}

.benefit-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.benefit-item:hover {
  background: rgba(143, 0, 0, 0.1);
  transform: translateX(5px);
}

.benefit-icon {
  color: #8f0000;
  font-size: 1rem;
  width: 16px;
}

.values-highlight {
  height: 100%;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid rgba(143, 0, 0, 0.3);
}

@media (max-width: 1024px) {
  .values-highlight {
    display:none;
  }
}

.values-highlight h4 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1.5rem;
  text-align: center;
}

.values-grid {
  height:90%;
  align-items: center;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.value-item {
  display: flex;
  height:85%;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.value-item:hover {
  background: rgba(143, 0, 0, 0.2);
  transform: scale(1.05);
}

.value-icon {
  width: 40px;
  height: 40px;
  background: rgba(143, 0, 0, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8f0000;
  transition: all 0.3s ease;
}

.value-item:hover .value-icon {
  background: rgba(143, 0, 0, 0.4);
  color: #ffffff;
}

/* Application Section */
.application-section {
  position: relative;
  grid-column: 1 / span 2;
}

.form-container {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.form-header {
  text-align: center;
  margin-bottom: 2rem;
}

.form-icon {
  font-size: 2rem;
  color: #8f0000;
  margin-bottom: 1rem;
}

.form-header h3 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
}

.form-header p {
  color: rgba(183, 185, 186, 0.8);
  font-size: 1rem;
}

/* Form Styles */
.application-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-section {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.section-title-small {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(143, 0, 0, 0.3);
}

.section-icon {
  color: #8f0000;
  font-size: 1.1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
}

.file-note {
  font-size: 0.8rem;
  color: rgba(183, 185, 186, 0.7);
  font-weight: normal;
}

.form-input,
.form-textarea {
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #ffffff;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

/* Fix dropdown background and text visibility */
.form-input[type="text"],
.form-input[type="email"],
.form-input[type="tel"],
.form-input[type="date"],
.form-textarea {
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
}

select.form-input {
  background: rgba(50, 50, 50, 0.9) !important;
  color: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

select.form-input option {
  background: rgba(50, 50, 50, 0.95) !important;
  color: #ffffff !important;
  padding: 0.5rem;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: rgba(143, 0, 0, 0.8);
  box-shadow: 0 0 0 2px rgba(143, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.08);
}

select.form-input:focus {
  background: rgba(50, 50, 50, 0.95) !important;
}

.form-input.error,
.form-textarea.error {
  border-color: #ff4444;
  box-shadow: 0 0 0 2px rgba(255, 68, 68, 0.2);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

/* File Upload Styles */
.file-upload-container {
  position: relative;
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  z-index: 2;
}

.file-upload-display {
  background: rgba(255, 255, 255, 0.05);
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-upload-display:hover {
  border-color: rgba(143, 0, 0, 0.5);
  background: rgba(255, 255, 255, 0.08);
}

.file-upload-display.has-file {
  border-color: rgba(34, 197, 94, 0.5);
  background: rgba(34, 197, 94, 0.1);
  border-style: solid;
}

.file-upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  position: relative;
}

.file-icon {
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.3s ease;
}

.file-icon.uploaded {
  color: #22c55e;
}

.file-text {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  max-width: 250px;
  word-break: break-all;
}

.file-size {
  font-size: 0.8rem;
  color: rgba(183, 185, 186, 0.7);
}

.remove-file-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  background: #ef4444;
  border: none;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: background 0.3s ease;
}

.remove-file-btn:hover {
  background: #dc2626;
}

.error-message {
  color: #ff4444;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.form-note {
  font-size: 0.8rem;
  color: rgba(183, 185, 186, 0.7);
  margin-top: 0.25rem;
  font-style: italic;
}

/* Checkbox and Radio Styles */
.checkbox-group,
.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
}

.checkbox-label,
.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  transition: color 0.3s ease;
}

.checkbox-label:hover,
.radio-label:hover {
  color: #ffffff;
}

.checkbox-label input[type="checkbox"],
.radio-label input[type="radio"] {
  opacity: 0;
  position: absolute;
}

.checkmark,
.radio-mark {
  width: 18px;
  height: 18px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  transition: all 0.3s ease;
  position: relative;
}

.radio-mark {
  border-radius: 50%;
}

.checkbox-label input[type="checkbox"]:checked + .checkmark,
.radio-label input[type="radio"]:checked + .radio-mark {
  background: #8f0000;
  border-color: #8f0000;
}

.checkbox-label input[type="checkbox"]:checked + .checkmark::after {
  content: "✓";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.radio-label input[type="radio"]:checked + .radio-mark::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
}

.radio-group-label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  display: block;
}

/* Signature Styles */
.signature-container {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
}

.signature-header p {
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.signature-canvas-container {
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.signature-canvas {
  display: block;
  cursor: crosshair;
  border-radius: 6px;
}

.signature-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
  pointer-events: none;
}

.signature-controls {
  margin-bottom: 1rem;
}

.clear-signature-btn {
  background: rgba(255, 68, 68, 0.2);
  border: 1px solid rgba(255, 68, 68, 0.3);
  color: #ff4444;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.clear-signature-btn:hover {
  background: rgba(255, 68, 68, 0.3);
  border-color: rgba(255, 68, 68, 0.5);
}

.signature-agreement {
  margin-top: 1rem;
}

.signature-text {
  font-size: 0.8rem;
  color: rgba(183, 185, 186, 0.8);
  line-height: 1.4;
  font-style: italic;
}

/* reCAPTCHA Styles */
.recaptcha-container {
  display: flex;
  justify-content: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Submit Button */
.form-submit {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.submit-btn {
  background: linear-gradient(135deg, #8f0000 0%, #a22a21 100%);
  border: none;
  color: #ffffff;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 200px;
  justify-content: center;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(143, 0, 0, 0.4);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.submit-btn.submitting {
  background: linear-gradient(135deg, #666666 0%, #888888 100%);
}

.submit-icon {
  font-size: 1rem;
}

/* Success/Error Messages */
.success-message,
.error-message-container {
  margin-top: 2rem;
  padding: 2rem;
  border-radius: 8px;
  text-align: center;
}

.success-message {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.error-message-container {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.success-content,
.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.success-icon {
  font-size: 3rem;
  color: #22c55e;
}

.error-icon {
  font-size: 3rem;
  color: #ef4444;
}

.success-content h3,
.error-content h3 {
  font-size: 1.5rem;
  color: #ffffff;
  margin: 0;
}

.success-content p,
.error-content p {
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.5;
}

.error-dismiss {
  background: #ef4444;
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.error-dismiss:hover {
  background: #dc2626;
}

/* Bottom CTA - keeping existing styles */
.bottom-cta {
  margin-top: 4rem;
  text-align: center;
  position: relative;
  z-index: 10;
}

.cta-content {
  background: rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  padding: 3rem;
  border: 1px solid rgba(143, 0, 0, 0.3);
}

.cta-content h3 {
  font-size: 2rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1rem;
}

.cta-content p {
  font-size: 1.1rem;
  color: rgba(183, 185, 186, 0.9);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 600px;
  margin: 0 auto;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #8f0000;
  line-height: 1;
}

.stat-label {
  font-size: 0.9rem;
  color: rgba(183, 185, 186, 0.8);
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .section-title {
    font-size: 2rem;
  }
  
  .section-subtitle {
    font-size: 1rem;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .values-grid {
    grid-template-columns: 1fr;
  }
  
  .cta-stats {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .stat-number {
    font-size: 2rem;
  }
  
  .checkbox-group,
  .radio-group {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .signature-canvas {
    width: 100%;
    max-width: 350px;
    height: 120px;
  }
  
  .form-container {
    padding: 1.5rem;
  }
}

@media (max-width: 480px) {
  .content-grid {
    gap: 2rem;
  }
  
  .form-section {
    padding: 1rem;
  }
  
  .signature-canvas {
    height: 100px;
  }
}
</style>
